const compileShader = (gl, type, source) => {
  const shader = gl.createShader(type);

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS) && !gl.isContextLost()) {
    const log = gl.getShaderInfoLog(shader);

    gl.deleteShader(shader);
    throw new Error(`Chart shader compile error: ${log}`);
  }

  return shader;
};

// Programs of a context by their sources: renderers of the same kind (one per
// indicator) share one program instead of compiling it again
const programCache = new WeakMap();

/**
 * Forgets the shared programs of `gl`; called before all resources are
 * created again (start, a restored context), when the old ones are gone.
 *
 * @param {WebGL2RenderingContext} gl
 */
export const forgetPrograms = (gl) => programCache.delete(gl);

/**
 * Compiles and links a WebGL2 program and looks up its uniforms.
 * Attribute locations are fixed in the shaders with `layout(location = N)`.
 *
 * @param {WebGL2RenderingContext} gl
 * @param {string} vertexSource
 * @param {string} fragmentSource
 * @param {string[]} uniformNames
 * @returns {{ program: WebGLProgram, uniforms: Object<string, WebGLUniformLocation> }}
 */
const compileProgram = (gl, vertexSource, fragmentSource, uniformNames) => {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS) && !gl.isContextLost()) {
    throw new Error(`Chart shader link error: ${gl.getProgramInfoLog(program)}`);
  }

  const uniforms = {};

  uniformNames.forEach((name) => {
    uniforms[name] = gl.getUniformLocation(program, name);
  });

  return { program, uniforms };
};

/**
 * The program of these sources, compiled once per context (compileProgram).
 * Shared programs are never deleted: they go with the context.
 *
 * @param {WebGL2RenderingContext} gl
 * @param {string} vertexSource
 * @param {string} fragmentSource
 * @param {string[]} uniformNames
 * @returns {{ program: WebGLProgram, uniforms: Object<string, WebGLUniformLocation> }}
 */
export const createProgram = (gl, vertexSource, fragmentSource, uniformNames) => {
  if (!programCache.has(gl)) programCache.set(gl, new Map());

  const programs = programCache.get(gl);
  const key = `${vertexSource}\n${fragmentSource}`;

  if (!programs.has(key)) {
    programs.set(key, compileProgram(gl, vertexSource, fragmentSource, uniformNames));
  }

  return programs.get(key);
};

// Fragment shader shared by all geometry: a flat colour from the vertex shader
export const FLAT_COLOR_FRAGMENT_SHADER = `#version 300 es
precision mediump float;

in vec4 vColor;
out vec4 outColor;

void main() {
  outColor = vColor;
}
`;
